// Answers two questions Janelle asked for directly: "what's our slowest day
// of the week" (the weekday chart) and "show me the trend by day/week/month"
// (the trend chart below it, mirroring the Day/Week/Month language already
// used on the Roster demand view). Covers BOTH data sources -- completed
// TaxiCaller jobs (every job in the fleet, not just subsidised ones) and
// Total Mobility trips (RideWise, the subsidised-scheme subset Fleet Pulse
// already tracked) -- with a toggle to look at either one alone or both
// together, since they answer different questions ("how busy is the whole
// fleet" vs "how busy is our TM work specifically").
import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { Briefcase, Accessibility, TrendingDown, TrendingUp, CalendarRange, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { eachDayOfInterval, format, getISOWeek, startOfISOWeek } from "date-fns";
import { RideWiseRow, TaxiCallerRow } from "./types";
import { fmtNum, safeArr } from "./helpers";

type Source = "both" | "jobs" | "tm";
type GroupBy = "day" | "week" | "month";

interface Props {
  tc: TaxiCallerRow[];
  rw: RideWiseRow[];
  /** Same pool as tc/rw but NOT narrowed to the selected years/months --
   *  only needed for the year-comparison mode below, same idea as
   *  OverviewTab's `allRw` prop. Falls back to tc/rw when omitted. */
  allTc?: TaxiCallerRow[];
  allRw?: RideWiseRow[];
  selectedYears?: string[];
  selectedMonths?: string[];
  loading: boolean;
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Distinct per-year colours (same palette OverviewTab uses for its own
// one-line-per-year chart, so the two tabs read consistently).
const YEAR_COLOURS = ["#eab308", "#3b82f6", "#22c55e", "#a855f7", "#ef4444", "#06b6d4", "#f97316", "#ec4899"];
// JS Date#getDay() is 0=Sun..6=Sat -- shift so Monday is index 0, matching
// how the rest of DMS (roster, capacity) treats the week.
const mondayFirstIndex = (jsDay: number) => (jsDay + 6) % 7;

const JOBS_COLOR = "#3b82f6"; // blue -- matches the "actual jobs" bar on the Roster demand chart
const TM_COLOR = "#eab308"; // gold -- matches Fleet Pulse's own accent used for RideWise elsewhere

const SOURCE_OPTIONS: { value: Source; label: string }[] = [
  { value: "both", label: "Jobs + TM" },
  { value: "jobs", label: "Jobs only" },
  { value: "tm", label: "TM only" },
];

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const escape = (v: string | number) => {
    const s = String(v ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PillGroup: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}> = ({ value, options, onChange }) => (
  <div className="flex gap-1">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
          value === opt.value
            ? "bg-primary/20 text-primary border-primary/50"
            : "bg-secondary/40 text-muted-foreground border-border hover:border-primary/30"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const StatCard: React.FC<{ title: string; value: string; sub?: string; icon: React.ReactNode }> = ({
  title,
  value,
  sub,
  icon,
}) => (
  <Card className="bg-card border border-border">
    <CardContent className="pt-3 pb-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] text-muted-foreground truncate">{title}</div>
          <div className="text-lg font-bold mt-0.5 truncate">{value}</div>
          {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
        </div>
        <div className="text-yellow-500 shrink-0">{icon}</div>
      </div>
    </CardContent>
  </Card>
);

const JobsTrendsTab: React.FC<Props> = ({ tc, rw, allTc, allRw, selectedYears, selectedMonths, loading }) => {
  const [source, setSource] = useState<Source>("both");
  const [groupBy, setGroupBy] = useState<GroupBy>("day");

  // Two or more real years selected up top -> compare mode: the trend
  // chart switches from a single continuous timeline to one line per year,
  // aligned by month (or week-of-year) so the same period lines up across
  // years -- e.g. Jun/Jul/Aug 2026 directly over Jun/Jul/Aug 2025.
  const compareYears = useMemo(() => {
    const ys = (selectedYears || []).filter((y) => y !== "all");
    return ys.length >= 2 ? [...new Set(ys)].sort() : [];
  }, [selectedYears]);
  const isComparing = compareYears.length >= 2;
  // Day-by-day doesn't align across years (weekdays fall on different
  // dates), so compare mode only offers Month/Week -- fall back off Day
  // automatically rather than showing a chart that doesn't mean anything.
  const effectiveGroupBy: GroupBy = isComparing && groupBy === "day" ? "month" : groupBy;

  // "Completed jobs" specifically -- driver_completed can also be false
  // (cancelled/no-show) or null (still open); only true counts as a
  // completed job for this view.
  const completedJobs = useMemo(() => safeArr(tc).filter((r) => r.driver_completed === true), [tc]);
  const tmTrips = safeArr(rw);

  const showJobs = source === "both" || source === "jobs";
  const showTm = source === "both" || source === "tm";

  // Weekday breakdown -- averaged per weekday OCCURRENCE (not a raw total),
  // so a range that doesn't divide evenly into whole weeks still gives a
  // fair "which day is slowest" comparison. Occurrences are counted across
  // every calendar day spanned by the data (not just days that happen to
  // have a job), so a weekday with zero jobs on some date still counts as
  // an occurrence rather than silently inflating other days' averages.
  const weekdayData = useMemo(() => {
    const jobDates = completedJobs.map((r) => r.pu_date).filter(Boolean) as string[];
    const tmDates = tmTrips.map((r) => r.trip_date).filter(Boolean) as string[];
    const allDates = [...jobDates, ...tmDates];
    if (allDates.length === 0) return [];

    const minDate = allDates.reduce((a, b) => (a < b ? a : b));
    const maxDate = allDates.reduce((a, b) => (a > b ? a : b));

    const occurrences = [0, 0, 0, 0, 0, 0, 0];
    for (const d of eachDayOfInterval({ start: new Date(`${minDate}T00:00:00`), end: new Date(`${maxDate}T00:00:00`) })) {
      occurrences[mondayFirstIndex(d.getDay())]++;
    }

    const jobsTotal = [0, 0, 0, 0, 0, 0, 0];
    for (const d of jobDates) jobsTotal[mondayFirstIndex(new Date(`${d}T00:00:00`).getDay())]++;
    const tmTotal = [0, 0, 0, 0, 0, 0, 0];
    for (const d of tmDates) tmTotal[mondayFirstIndex(new Date(`${d}T00:00:00`).getDay())]++;

    return WEEKDAY_LABELS.map((label, i) => ({
      weekday: label,
      jobsAvg: occurrences[i] ? jobsTotal[i] / occurrences[i] : 0,
      tmAvg: occurrences[i] ? tmTotal[i] / occurrences[i] : 0,
      jobsTotal: jobsTotal[i],
      tmTotal: tmTotal[i],
      occurrences: occurrences[i],
    }));
  }, [completedJobs, tmTrips]);

  const busiestSlowest = useMemo(() => {
    if (weekdayData.length === 0) return null;
    const metricFor = (r: (typeof weekdayData)[number]) =>
      source === "jobs" ? r.jobsAvg : source === "tm" ? r.tmAvg : r.jobsAvg + r.tmAvg;
    let busiest = weekdayData[0];
    let slowest = weekdayData[0];
    for (const r of weekdayData) {
      if (metricFor(r) > metricFor(busiest)) busiest = r;
      if (metricFor(r) < metricFor(slowest)) slowest = r;
    }
    return { busiest, slowest, busiestVal: metricFor(busiest), slowestVal: metricFor(slowest) };
  }, [weekdayData, source]);

  // Trend over time -- one point per day/week/month depending on groupBy.
  const trendData = useMemo(() => {
    const bucketOf = (dateStr: string): string => {
      if (groupBy === "day") return dateStr;
      if (groupBy === "month") return dateStr.slice(0, 7);
      return format(startOfISOWeek(new Date(`${dateStr}T00:00:00`)), "yyyy-MM-dd");
    };

    const map = new Map<string, { jobs: number; tm: number }>();
    for (const r of completedJobs) {
      if (!r.pu_date) continue;
      const key = bucketOf(r.pu_date);
      const entry = map.get(key) || { jobs: 0, tm: 0 };
      entry.jobs++;
      map.set(key, entry);
    }
    for (const r of tmTrips) {
      if (!r.trip_date) continue;
      const key = bucketOf(r.trip_date);
      const entry = map.get(key) || { jobs: 0, tm: 0 };
      entry.tm++;
      map.set(key, entry);
    }

    return Array.from(map.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([key, v]) => {
        let label = key;
        if (groupBy === "month") label = format(new Date(`${key}-01T00:00:00`), "MMM yyyy");
        else if (groupBy === "week") label = `w/o ${format(new Date(`${key}T00:00:00`), "d MMM")}`;
        else label = format(new Date(`${key}T00:00:00`), "d MMM");
        return { bucket: label, jobs: v.jobs, tm: v.tm };
      });
  }, [completedJobs, tmTrips, groupBy]);

  // Compare-mode trend: one line per selected year, aligned by month or
  // ISO week-of-year rather than a continuous timeline, so June 2026 sits
  // directly above June 2025. Pulls from allTc/allRw (not narrowed to the
  // currently-selected years) since it needs every compare year's data,
  // then narrows to compareYears + selectedMonths itself.
  const compareTrendData = useMemo(() => {
    if (!isComparing) return [] as Record<string, number | string>[];

    const monthsAllowed =
      !selectedMonths || selectedMonths.includes("all") || selectedMonths.length === 0 ? null : new Set(selectedMonths);

    const tcPool = safeArr(allTc && allTc.length ? allTc : tc).filter((r) => r.driver_completed === true);
    const rwPool = safeArr(allRw && allRw.length ? allRw : rw);

    const bucketKeyOf = (dateStr: string) =>
      effectiveGroupBy === "month" ? dateStr.slice(5, 7) : String(getISOWeek(new Date(`${dateStr}T00:00:00`))).padStart(2, "0");
    const bucketLabelOf = (key: string) => (effectiveGroupBy === "month" ? MONTH_LABELS[Number(key) - 1] || key : `Wk ${Number(key)}`);

    // bucketKey -> year -> totals
    const map = new Map<string, Map<string, { jobs: number; tm: number }>>();
    const addRow = (dateStr: string, field: "jobs" | "tm") => {
      const year = dateStr.slice(0, 4);
      if (!compareYears.includes(year)) return;
      const monthStr = dateStr.slice(5, 7);
      if (monthsAllowed && !monthsAllowed.has(monthStr)) return;
      const key = bucketKeyOf(dateStr);
      let yearMap = map.get(key);
      if (!yearMap) {
        yearMap = new Map();
        map.set(key, yearMap);
      }
      let entry = yearMap.get(year);
      if (!entry) {
        entry = { jobs: 0, tm: 0 };
        yearMap.set(year, entry);
      }
      entry[field]++;
    };

    for (const r of tcPool) {
      if (r.pu_date) addRow(r.pu_date, "jobs");
    }
    for (const r of rwPool) {
      if (r.trip_date) addRow(r.trip_date, "tm");
    }

    return Array.from(map.keys())
      .sort()
      .map((key) => {
        const row: Record<string, number | string> = { bucket: bucketLabelOf(key) };
        for (const y of compareYears) {
          const entry = map.get(key)?.get(y);
          row[`jobs_${y}`] = entry?.jobs ?? 0;
          row[`tm_${y}`] = entry?.tm ?? 0;
        }
        return row;
      });
  }, [isComparing, effectiveGroupBy, compareYears, selectedMonths, allTc, allRw, tc, rw]);

  const exportWeekdayCsv = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadCSV(
      `busiest-slowest-day-of-week_${today}.csv`,
      ["Weekday", "Jobs Avg/Day", "TM Avg/Day", "Jobs Total", "TM Total", "Occurrences in Period"],
      weekdayData.map((r) => [r.weekday, r.jobsAvg.toFixed(2), r.tmAvg.toFixed(2), r.jobsTotal, r.tmTotal, r.occurrences])
    );
  };

  const exportTrendCsv = () => {
    const today = new Date().toISOString().slice(0, 10);
    if (isComparing) {
      const bucketLabel = effectiveGroupBy === "month" ? "Month" : "Week of Year";
      downloadCSV(
        `jobs-trend-compare-${compareYears.join("-vs-")}_${today}.csv`,
        [bucketLabel, ...compareYears.flatMap((y) => [`Jobs ${y}`, `TM ${y}`])],
        compareTrendData.map((r) => [r.bucket, ...compareYears.flatMap((y) => [r[`jobs_${y}`], r[`tm_${y}`]])])
      );
      return;
    }
    const bucketLabel = groupBy === "day" ? "Date" : groupBy === "week" ? "Week Of" : "Month";
    downloadCSV(
      `jobs-trend-by-${groupBy}_${today}.csv`,
      [bucketLabel, "Jobs", "TM"],
      trendData.map((r) => [r.bucket, r.jobs, r.tm])
    );
  };

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading jobs &amp; trends…</CardContent>
      </Card>
    );
  }

  if (completedJobs.length === 0 && tmTrips.length === 0) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">
          No completed jobs or TM trips in the selected period.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        "Jobs" = every completed TaxiCaller job across the whole fleet. "TM" = Total Mobility trips only (the
        subsidised-scheme subset). Both respect the filters above.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] text-muted-foreground mb-1">Source</div>
          <PillGroup value={source} options={SOURCE_OPTIONS} onChange={(v) => setSource(v as Source)} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard
          title="Completed Jobs"
          value={fmtNum(completedJobs.length)}
          icon={<Briefcase className="w-4 h-4" />}
        />
        <StatCard title="TM Trips" value={fmtNum(tmTrips.length)} icon={<Accessibility className="w-4 h-4" />} />
        {busiestSlowest && (
          <>
            <StatCard
              title="Busiest Day"
              value={busiestSlowest.busiest.weekday}
              sub={`${busiestSlowest.busiestVal.toFixed(1)} avg/day`}
              icon={<TrendingUp className="w-4 h-4 text-green-500" />}
            />
            <StatCard
              title="Slowest Day"
              value={busiestSlowest.slowest.weekday}
              sub={`${busiestSlowest.slowestVal.toFixed(1)} avg/day`}
              icon={<TrendingDown className="w-4 h-4 text-red-500" />}
            />
          </>
        )}
      </div>

      <Card className="bg-card border border-border">
        <CardContent className="pt-4">
          <div className="mb-3 flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-semibold">Busiest / Slowest Day of the Week</div>
              <div className="text-[11px] text-muted-foreground">
                Average per weekday across the selected period -- not a raw total, so partial weeks don't skew it.
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={exportWeekdayCsv} disabled={weekdayData.length === 0}>
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export CSV
            </Button>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekdayData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="weekday" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                  formatter={(value: number, name: string) => [`${Number(value).toFixed(2)} avg/day`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {showJobs && <Bar dataKey="jobsAvg" name="Jobs" fill={JOBS_COLOR} radius={[4, 4, 0, 0]} />}
                {showTm && <Bar dataKey="tmAvg" name="TM" fill={TM_COLOR} radius={[4, 4, 0, 0]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border border-border">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <div className="text-sm font-semibold flex items-center gap-1.5">
                <CalendarRange className="w-4 h-4 text-yellow-500" />
                Trend Over Time
              </div>
              <div className="text-[11px] text-muted-foreground">
                {isComparing
                  ? `Comparing ${compareYears.join(" vs ")} -- same ${effectiveGroupBy} lined up across years`
                  : "Grouped by day, week, or month"}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <PillGroup
                value={effectiveGroupBy}
                options={isComparing ? GROUP_OPTIONS.filter((o) => o.value !== "day") : GROUP_OPTIONS}
                onChange={(v) => setGroupBy(v as GroupBy)}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={exportTrendCsv}
                disabled={isComparing ? compareTrendData.length === 0 : trendData.length === 0}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export CSV
              </Button>
            </div>
          </div>
          {isComparing && (
            <p className="text-[11px] text-muted-foreground mb-2">
              Pick 2+ years up in the filters above to compare them here -- e.g. Years: 2025, 2026 and Months: Jun,
              Jul, Aug for a Jun-Aug year-on-year comparison. Untick down to one year to go back to a normal
              timeline.
            </p>
          )}
          <div className="h-[340px] w-full">
            {isComparing ? (
              compareTrendData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                  No data to chart for this selection.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={compareTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {compareYears.flatMap((y, idx) => {
                      const colour = YEAR_COLOURS[idx % YEAR_COLOURS.length];
                      return [
                        showJobs && (
                          <Line
                            key={`jobs_${y}`}
                            type="monotone"
                            dataKey={`jobs_${y}`}
                            name={`Jobs ${y}`}
                            stroke={colour}
                            strokeWidth={2}
                            dot={false}
                          />
                        ),
                        showTm && (
                          <Line
                            key={`tm_${y}`}
                            type="monotone"
                            dataKey={`tm_${y}`}
                            name={`TM ${y}`}
                            stroke={colour}
                            strokeWidth={2}
                            strokeDasharray="5 4"
                            dot={false}
                          />
                        ),
                      ];
                    })}
                  </LineChart>
                </ResponsiveContainer>
              )
            ) : trendData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No data to chart for this selection.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                  <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {showJobs && (
                    <Line type="monotone" dataKey="jobs" name="Jobs" stroke={JOBS_COLOR} strokeWidth={2} dot={false} />
                  )}
                  {showTm && <Line type="monotone" dataKey="tm" name="TM" stroke={TM_COLOR} strokeWidth={2} dot={false} />}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JobsTrendsTab;
